package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.domain.realestate.RealEstateEventBus
import com.daytimegaming.ourdailyfinances.domain.usecase.RealEstateUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class UpdateRealEstateHoldingState(
    val holding: RealEstateHolding? = null,
    val nickname: String = "",
    val addressLine1: String = "",
    val addressLine2: String = "",
    val city: String = "",
    val state: String = "",
    val zipCode: String = "",
    val county: String = "",
    val purchasePrice: String = "",
    val mortgageAmount: String = "",
    val mortgageInterestRate: String = "",
    val isLoading: Boolean = false,
    val isSuccess: Boolean = false,
    val isDeleted: Boolean = false,
    val error: String? = null
) {
    val isSaveEnabled: Boolean
        get() {
            if (holding == null) return false
            val hasValidNickname = nickname.isNotBlank()
            val hasValidPurchasePrice = purchasePrice.filter { it.isDigit() }.toDoubleOrNull() != null
            val hasValidMortgageAmount = mortgageAmount.isEmpty() || mortgageAmount.filter { it.isDigit() }.toDoubleOrNull() != null
            val cleanInterestRate = mortgageInterestRate.filter { it.isDigit() || it == '.' }
            val hasValidMortgageInterestRate = mortgageInterestRate.isEmpty() || cleanInterestRate.toDoubleOrNull() != null

            if (!hasValidNickname || !hasValidPurchasePrice || !hasValidMortgageAmount || !hasValidMortgageInterestRate || isLoading) {
                return false
            }

            val currentPurchasePrice = purchasePrice.filter { it.isDigit() }.toDoubleOrNull() ?: 0.0
            val currentMortgageAmount = mortgageAmount.filter { it.isDigit() }.toDoubleOrNull()
            val currentInterestRate = mortgageInterestRate.filter { it.isDigit() || it == '.' }.toDoubleOrNull()

            val isChanged = nickname.trim() != holding.nickname ||
                    currentPurchasePrice != holding.purchasePrice ||
                    currentMortgageAmount != holding.mortgageAmount ||
                    currentInterestRate != holding.mortgageInterestRate

            return isChanged
        }

    val hasAnyInput: Boolean
        get() = isSaveEnabled
}

class UpdateRealEstateHoldingViewModel(
    private val realEstateHoldingId: String,
    private val realEstateUseCase: RealEstateUseCase,
    private val realEstateEventBus: RealEstateEventBus
) : ViewModel() {

    private val _state = MutableStateFlow(UpdateRealEstateHoldingState())
    val state = _state.asStateFlow()

    init {
        load()
    }

    private fun load() {
        _state.update { it.copy(isLoading = true) }
        viewModelScope.launch {
            realEstateUseCase.GetRealEstateHolding(realEstateHoldingId).collect { response ->
                when (response) {
                    is Response.Success -> {
                        val holding = response.data
                        _state.update {
                            it.copy(
                                holding = holding,
                                nickname = holding.nickname,
                                addressLine1 = holding.addressLine1,
                                addressLine2 = holding.addressLine2.orEmpty(),
                                city = holding.city,
                                state = holding.state,
                                zipCode = holding.zipCode,
                                county = holding.county.orEmpty(),
                                purchasePrice = formatAmount(holding.purchasePrice),
                                mortgageAmount = holding.mortgageAmount?.let { amt -> formatAmount(amt) }.orEmpty(),
                                mortgageInterestRate = holding.mortgageInterestRate?.let { rate -> formatRate(rate) }.orEmpty(),
                                isLoading = false
                            )
                        }
                    }
                    is Response.Error -> {
                        _state.update { it.copy(isLoading = false, error = response.message) }
                    }
                    is Response.Loading -> {
                        _state.update { it.copy(isLoading = true) }
                    }
                }
            }
        }
    }

    fun onNicknameChange(value: String) {
        _state.update { it.copy(nickname = value) }
    }

    fun onPurchasePriceChange(value: String) {
        val digits = value.filter { it.isDigit() }
        val formatted = if (digits.isEmpty()) {
            ""
        } else {
            val parsed = digits.toLongOrNull() ?: 0L
            val formattedNumber = parsed.toString().reversed().chunked(3).joinToString(",").reversed()
            "$$formattedNumber"
        }
        _state.update { it.copy(purchasePrice = formatted) }
    }

    fun onMortgageAmountChange(value: String) {
        val digits = value.filter { it.isDigit() }
        val formatted = if (digits.isEmpty()) {
            ""
        } else {
            val parsed = digits.toLongOrNull() ?: 0L
            val formattedNumber = parsed.toString().reversed().chunked(3).joinToString(",").reversed()
            "$$formattedNumber"
        }
        _state.update { it.copy(mortgageAmount = formatted) }
    }

    fun onMortgageInterestRateChange(value: String) {
        val clean = value.filter { it.isDigit() || it == '.' }
        val formatted = if (clean.isEmpty()) {
            ""
        } else {
            val dotIndex = clean.indexOf('.')
            val base = if (dotIndex == -1) {
                clean
            } else {
                val beforeDot = clean.substring(0, dotIndex)
                var afterDot = clean.substring(dotIndex + 1).filter { it.isDigit() }
                if (afterDot.length > 2) {
                    afterDot = afterDot.substring(0, 2)
                }
                "$beforeDot.$afterDot"
            }
            "%$base"
        }
        _state.update { it.copy(mortgageInterestRate = formatted) }
    }

    fun updateHolding() {
        val current = _state.value
        val holding = current.holding ?: return
        if (!current.isSaveEnabled) return

        _state.update { it.copy(isLoading = true, error = null) }

        viewModelScope.launch {
            try {
                val purchasePriceDouble = current.purchasePrice.filter { it.isDigit() }.toDoubleOrNull() ?: 0.0
                val mortgageAmountDouble = current.mortgageAmount.filter { it.isDigit() }.toDoubleOrNull()
                val mortgageInterestRateDouble = current.mortgageInterestRate
                    .filter { it.isDigit() || it == '.' }
                    .toDoubleOrNull()

                val response = realEstateUseCase.UpdateRealEstateHolding(
                    realEstateHoldingId = realEstateHoldingId,
                    nickname = current.nickname.trim(),
                    addressLine1 = null,
                    addressLine2 = null,
                    city = null,
                    state = null,
                    zipCode = null,
                    county = null,
                    purchasePrice = purchasePriceDouble,
                    mortgageAmount = mortgageAmountDouble,
                    mortgageInterestRate = mortgageInterestRateDouble
                )

                when (response) {
                    is Response.Success -> {
                        realEstateEventBus.emitEvent()
                        _state.update { it.copy(isLoading = false, isSuccess = true) }
                    }
                    is Response.Error -> {
                        _state.update { it.copy(isLoading = false, error = response.message) }
                    }
                    is Response.Loading -> {
                        // Handled by isLoading = true
                    }
                }
            } catch (e: Exception) {
                _state.update { it.copy(isLoading = false, error = e.message ?: "An unexpected error occurred") }
            }
        }
    }

    fun deleteHolding() {
        _state.update { it.copy(isLoading = true, error = null) }
        viewModelScope.launch {
            val response = realEstateUseCase.DeleteRealEstateHolding(realEstateHoldingId)
            when (response) {
                is Response.Success -> {
                    realEstateEventBus.emitEvent()
                    _state.update { it.copy(isLoading = false, isDeleted = true) }
                }
                is Response.Error -> {
                    _state.update { it.copy(isLoading = false, error = response.message) }
                }
                is Response.Loading -> {
                    // Handled by isLoading = true
                }
            }
        }
    }

    private fun formatAmount(value: Double): String {
        val parsed = value.toLong()
        val formattedNumber = parsed.toString().reversed().chunked(3).joinToString(",").reversed()
        return "$$formattedNumber"
    }

    private fun formatRate(value: Double): String {
        return "%$value"
    }
}
